/**
 * Tracking Link Service
 * 
 * Provides functionality to detect carriers and generate tracking URLs
 * for various shipping carriers in Canada.
 * 
 * @module TrackingLinkService
 */

import { Carrier, CarrierConfig } from '../@types/shipment';

/**
 * Carrier configurations with detection patterns and URL templates
 */
const CARRIER_CONFIGS: CarrierConfig[] = [
  {
    name: 'PUROLATOR',
    displayName: 'Purolator',
    urlPattern: 'https://www.purolator.com/en/shipping/tracker?pin={trackingId}',
    trackingIdPatterns: [/^904\d+$/, /^905\d+$/, /^609\d{9}$/, /^610\d{9}$/],
    shipZoneKeywords: ['PUROL', 'PUROLATOR'],
    trackingIdPrefixes: ['904', '905', '609', '610']
  },
  {
    name: 'CANADA_POST',
    displayName: 'Canada Post',
    urlPattern: 'https://www.canadapost-postescanada.ca/track-reperage/en#/details/{trackingId}',
    trackingIdPatterns: [/^[A-Z0-9]{16}$/i, /^\d{13}$/, /^[A-Z]{2}\d{9}[A-Z]{2}$/i],
    shipZoneKeywords: ['CANADA POST', 'CP', 'POSTES', 'CANADAPOST']
  },
  {
    name: 'FEDEX',
    displayName: 'FedEx',
    urlPattern: 'https://www.fedex.com/fedextrack/?tracknumbers={trackingId}',
    trackingIdPatterns: [/^\d{12}$/, /^\d{14}$/, /^[0-9]{12}$/],
    shipZoneKeywords: ['FEDEX', 'FED EX', 'FEDERAL EXPRESS']
  },
  {
    name: 'UPS',
    displayName: 'UPS',
    urlPattern: 'https://www.ups.com/track?loc=en_CA&tracknum={trackingId}',
    trackingIdPatterns: [/^1Z[A-Z0-9]{16}$/i, /^T\d{10}$/i],
    shipZoneKeywords: ['UPS'],
    trackingIdPrefixes: ['1Z', 'T']
  },
  {
    name: 'DHL',
    displayName: 'DHL Express',
    urlPattern: 'https://www.dhl.com/ca-en/home/tracking.html?tracking-id={trackingId}',
    trackingIdPatterns: [/^\d{10}$/, /^\d{11}$/],
    shipZoneKeywords: ['DHL']
  },
  {
    name: 'DAY_ROSS',
    displayName: 'Day & Ross',
    urlPattern: 'https://dayross.com/view-shipment-tracking?division=Freight&probillNumber={trackingId}',
    trackingIdPatterns: [],
    shipZoneKeywords: ['DAYRO', 'DAY ROSS', 'DAY&ROSS', 'DAYROSS']
  },
  {
    name: 'GARDEWINE',
    displayName: 'Gardewine',
    urlPattern: 'https://gardewineweb.gardewine.com/scripts/cgiip.exe/boldetail.htm?wbtn=PRO&wpro1={trackingId}',
    trackingIdPatterns: [/^\d{10}$/],
    shipZoneKeywords: ['GARDE', 'GARDEWINE']
  },
  {
    name: 'MIDLAND',
    displayName: 'Midland',
    urlPattern: 'https://ship.midlandtransport.com/Tracking/TrackClientTrackings?TrackingNumber={trackingId}&Lang=0',
    trackingIdPatterns: [/^\d{8}$/],
    shipZoneKeywords: ['MIDLA', 'MIDLAND']
  },
  {
    name: 'REGIONAL',
    displayName: 'Regional Carrier',
    urlPattern: '',
    trackingIdPatterns: [],
    shipZoneKeywords: ['VANCOUVER', 'MONTREAL', 'SASKATOON', 'REGINA']
  }
];

/**
 * Tracking Link Service
 * Handles carrier detection and tracking URL generation
 */
export class TrackingLinkService {
  /**
   * Extract Purolator tracking number from a longer string
   * Purolator tracking numbers follow this STRICT rule:
   * - ONLY use "609" or "610" followed by exactly 9 digits (total 12 digits)
   * - "904" or "905" followed by digits (freight format) - keep as is
   * 
   * Example: From "118141039946090886280600000000", extract "609088628060"
   *          (only the "609" or "610" + 9 digits part)
   * 
   * @param trackingId - Original tracking ID that may contain the Purolator number
   * @returns Extracted tracking number or original if no valid pattern found
   */
  static extractPurolatorTrackingNumber(trackingId: string): string {
    if (!trackingId || !trackingId.trim()) {
      return trackingId;
    }

    const trimmed = trackingId.trim();
    
    // Skip if it's a descriptive string (not a tracking number)
    if (!/^\d+$/.test(trimmed)) {
      return trimmed;
    }
    
    // If it starts with "904" or "905" (freight format), keep it as is
    if (/^(904|905)\d+$/.test(trimmed)) {
      console.log('[TrackingLinkService] Purolator tracking number already in freight (904/905) format:', trimmed);
      return trimmed;
    }
    
    // If it's exactly 12 digits starting with "609" or "610", it's already correct
    if (/^(609|610)\d{9}$/.test(trimmed)) {
      console.log('[TrackingLinkService] Purolator tracking number already in correct 609/610 format:', trimmed);
      return trimmed;
    }
    
    // STRICT RULE: Look for "609" or "610" followed by exactly 9 digits (total 12 digits) anywhere in the string
    // This is the ONLY pattern we use for Purolator (except 904 format)
    // Example: "118141039946090886280600000000" -> extract "609088628060"
    const purolatorPattern = /(609|610)\d{9}/;
    const match = trimmed.match(purolatorPattern);
    
    if (match) {
      const extracted = match[0]; // This will be exactly "609"/"610" + 9 digits = 12 digits
      console.log('[TrackingLinkService] Extracted Purolator tracking number (609/610 + 9 digits):', {
        original: trimmed,
        extracted: extracted
      });
      return extracted;
    }
    
    // If no "609" or "610" + 9 digits pattern found, return original
    // We don't try other patterns - only "609"/"610" + 9 digits or "904" format
    console.log('[TrackingLinkService] No Purolator "609/610" + 9 digits pattern found, returning original:', trimmed);
    return trimmed;
  }

  /**
   * Extract Day & Ross tracking number from a longer string
   * Day & Ross tracking numbers follow this STRICT rule:
   * - If tracking ID starts with "WPG", extract only "WPG" + exactly 7 digits
   * - Example: From "WPG1234567890123", extract "WPG1234567"
   * 
   * @param trackingId - Original tracking ID that may contain extra characters
   * @returns Extracted tracking number or original if no valid pattern found
   */
  static extractDayRossTrackingNumber(trackingId: string): string {
    if (!trackingId || !trackingId.trim()) {
      return trackingId;
    }

    const trimmed = trackingId.trim().toUpperCase();
    
    // Check if it starts with "WPG"
    if (trimmed.startsWith('WPG')) {
      // If it's already WPG + exactly 7 digits, return as is
      if (/^WPG\d{7}$/.test(trimmed)) {
        console.log('[TrackingLinkService] Day & Ross tracking number already in correct format:', trimmed);
        return trimmed;
      }
      
      // Extract WPG + exactly 7 digits (first 7 digits after WPG)
      const match = trimmed.match(/^WPG(\d{7})/);
      if (match) {
        const extracted = 'WPG' + match[1]; // WPG + 7 digits
        console.log('[TrackingLinkService] Extracted Day & Ross tracking number (WPG + 7 digits):', {
          original: trimmed,
          extracted: extracted
        });
        return extracted;
      }
      
      // If it starts with WPG but has less than 7 digits, return as is (invalid format)
      console.warn('[TrackingLinkService] Day & Ross tracking number starts with WPG but has less than 7 digits:', trimmed);
      return trimmed;
    }
    
    // If no WPG pattern found, return original
    return trimmed;
  }

  /**
   * Check if a Purolator tracking number needs correction
   * 
   * @param trackingId - Tracking number to check
   * @param shipZone - Ship zone to verify it's Purolator
   * @returns Object with needsCorrection flag and suggested tracking number
   */
  static checkPurolatorTrackingNumber(trackingId: string, shipZone: string): {
    needsCorrection: boolean;
    original: string;
    suggested: string;
  } {
    const zoneUpper = (shipZone || '').toUpperCase().trim();
    const isPurolator = zoneUpper.includes('PUROL') || zoneUpper.includes('PUROLATOR');
    
    if (!isPurolator) {
      return {
        needsCorrection: false,
        original: trackingId,
        suggested: trackingId
      };
    }

    const trimmed = trackingId.trim();
    const extracted = this.extractPurolatorTrackingNumber(trackingId);
    
    // Check if correction is needed:
    // 1. If extracted is different from original (extraction happened)
    // 2. If original is very long (30+ chars) and extracted is shorter (extraction happened)
    // 3. If original doesn't match valid Purolator patterns but extracted does
    const needsCorrection = (
      extracted !== trimmed && 
      (extracted.length < trimmed.length || /^(609\d{9}|610\d{9}|904\d+|905\d+)$/.test(extracted))
    );
    
    return {
      needsCorrection,
      original: trackingId,
      suggested: extracted
    };
  }

  /**
   * Detect carrier from ShipZone and TrackingID
   * ShipZone takes priority as it's the most reliable indicator from the CSV
   * 
   * @param shipZone - Ship zone/carrier identifier from CSV
   * @param trackingId - Tracking number
   * @returns Detected carrier type
   */
  static detectCarrier(shipZone: string, trackingId: string): Carrier {
    try {
      const zoneUpper = (shipZone || '').toUpperCase().trim();
      const trackingUpper = (trackingId || '').toUpperCase().trim();

      console.log('[TrackingLinkService] Detecting carrier:', { shipZone: zoneUpper, trackingId: trackingUpper });

      // FIRST: Check by ShipZone keywords (most reliable - comes directly from CSV)
      // This takes priority to avoid false matches from tracking ID patterns
      for (const config of CARRIER_CONFIGS) {
        if (config.shipZoneKeywords && config.shipZoneKeywords.length > 0) {
          for (const keyword of config.shipZoneKeywords) {
            if (zoneUpper.includes(keyword)) {
              console.log('[TrackingLinkService] Detected by ShipZone keyword:', config.name, 'keyword:', keyword);
              return config.name;
            }
          }
        }
      }

      // SECOND: Check by tracking ID prefix (for carriers with distinctive prefixes)
      for (const config of CARRIER_CONFIGS) {
        if (config.trackingIdPrefixes && config.trackingIdPrefixes.length > 0) {
          for (const prefix of config.trackingIdPrefixes) {
            if (trackingUpper.startsWith(prefix)) {
              // Special handling for Purolator - check if it contains 609 or 610 pattern
              if (config.name === 'PUROLATOR' && (trackingUpper.includes('609') || trackingUpper.includes('610'))) {
                console.log('[TrackingLinkService] Detected Purolator by 609/610 pattern in tracking ID');
                return config.name;
              }
              console.log('[TrackingLinkService] Detected by tracking ID prefix:', config.name, 'prefix:', prefix);
              return config.name;
            }
          }
        }
      }

      // THIRD: Check by tracking ID pattern (least reliable - can have false matches)
      // Only use this as a last resort
      for (const config of CARRIER_CONFIGS) {
        if (config.trackingIdPatterns && config.trackingIdPatterns.length > 0) {
          for (const pattern of config.trackingIdPatterns) {
            if (pattern.test(trackingId)) {
              console.log('[TrackingLinkService] Detected by tracking ID pattern:', config.name);
              return config.name;
            }
          }
        }
      }

      console.warn('[TrackingLinkService] Could not detect carrier, defaulting to UNKNOWN');
      return 'UNKNOWN';
    } catch (error) {
      console.error('[TrackingLinkService] Error detecting carrier:', error);
      return 'UNKNOWN';
    }
  }

  /**
   * Generate tracking URL for a carrier and tracking ID
   * 
   * @param carrier - Carrier type
   * @param trackingId - Tracking number
   * @returns Tracking URL or empty string if not available
   */
  static generateTrackingUrl(carrier: Carrier, trackingId: string): string {
    try {
      if (!trackingId || !trackingId.trim()) {
        console.warn('[TrackingLinkService] Empty tracking ID provided');
        return '';
      }

      const config = CARRIER_CONFIGS.find(c => c.name === carrier);
      
      if (!config) {
        console.warn('[TrackingLinkService] No config found for carrier:', carrier);
        return '';
      }

      // Special handling for Purolator tracking IDs starting with "904" or "905"
      // Check the ORIGINAL tracking ID before normalization to determine URL pattern
      let urlPattern = config.urlPattern;
      if (carrier === 'PUROLATOR') {
        const originalTrackingId = trackingId.trim();
        // Check if original tracking ID starts with "904" or "905" (before any normalization)
        if (originalTrackingId.startsWith('904') || originalTrackingId.startsWith('905')) {
          // Use Purolator Freight tracking URL for tracking IDs starting with 904/905
          urlPattern = 'https://www.purolatorfreight.com/scripts/cgiip.exe/boldetail.htm?wbtn=PRO&wpro1={trackingId}';
          console.log('[TrackingLinkService] Using Purolator Freight URL for freight-prefixed tracking ID:', originalTrackingId);
        } else {
          // Use regular Purolator tracking URL for other tracking IDs
          urlPattern = 'https://www.purolator.com/en/shipping/tracker?pin={trackingId}';
          console.log('[TrackingLinkService] Using regular Purolator URL for non-freight tracking ID:', originalTrackingId);
        }
      }

      // For Purolator, extract the correct tracking number if needed (after determining URL pattern)
      // For Day & Ross, extract WPG + 7 digits if tracking ID starts with WPG
      let normalizedTrackingId = trackingId.trim();
      if (carrier === 'PUROLATOR') {
        normalizedTrackingId = this.extractPurolatorTrackingNumber(trackingId);
      } else if (carrier === 'DAY_ROSS') {
        normalizedTrackingId = this.extractDayRossTrackingNumber(trackingId);
      }

      if (!urlPattern) {
        console.warn('[TrackingLinkService] No URL pattern for carrier:', carrier);
        return '';
      }

      // Generate URL with tracking ID as query parameter
      let url = urlPattern;
      if (urlPattern.includes('{trackingId}')) {
        url = urlPattern.replace('{trackingId}', encodeURIComponent(normalizedTrackingId));
      }
      
      console.log('[TrackingLinkService] Generated URL:', url, 'for tracking ID:', normalizedTrackingId);
      return url;
    } catch (error) {
      console.error('[TrackingLinkService] Error generating tracking URL:', error);
      return '';
    }
  }

  /**
   * Validate tracking number format for a carrier
   * 
   * @param trackingId - Tracking number to validate
   * @param carrier - Carrier type
   * @returns True if tracking number format is valid
   */
  static validateTrackingNumber(trackingId: string, carrier: Carrier): boolean {
    try {
      if (!trackingId || !trackingId.trim()) {
        return false;
      }

      const config = CARRIER_CONFIGS.find(c => c.name === carrier);
      
      if (!config || config.trackingIdPatterns.length === 0) {
        // If no patterns defined, accept any non-empty string
        return true;
      }

      // Check if tracking ID matches any pattern
      for (const pattern of config.trackingIdPatterns) {
        if (pattern.test(trackingId.trim())) {
          return true;
        }
      }

      // Also check prefixes if available
      if (config.trackingIdPrefixes) {
        const trackingUpper = trackingId.toUpperCase().trim();
        for (const prefix of config.trackingIdPrefixes) {
          if (trackingUpper.startsWith(prefix)) {
            return true;
          }
        }
      }

      return false;
    } catch (error) {
      console.error('[TrackingLinkService] Error validating tracking number:', error);
      return false;
    }
  }

  /**
   * Get carrier display name
   * 
   * @param carrier - Carrier type
   * @returns Display name for the carrier
   */
  static getCarrierDisplayName(carrier: Carrier): string {
    const config = CARRIER_CONFIGS.find(c => c.name === carrier);
    return config?.displayName || carrier;
  }

  /**
   * Get all supported carriers
   * 
   * @returns Array of carrier configurations
   */
  static getSupportedCarriers(): CarrierConfig[] {
    return CARRIER_CONFIGS;
  }

  /**
   * Check if tracking URL is available for a carrier
   * 
   * @param carrier - Carrier type
   * @returns True if tracking URL can be generated
   */
  static hasTrackingUrl(carrier: Carrier): boolean {
    const config = CARRIER_CONFIGS.find(c => c.name === carrier);
    return config?.urlPattern ? true : false;
  }
}

